<!--
                               o8o                 .
                               `"'               .o8
   .oooo.o  .ooooo.  oooo d8b oooo  oo.ooooo.  .o888oo
  d88(  "8 d88' `"Y8 `888""8P `888   888' `88b   888
  `"Y88b.  888        888      888   888   888   888
  o.  )88b 888   .o8  888      888   888   888   888 .
  8""888P' `Y8bod8P' d888b    o888o  888bod8P'   "888"
                                     888
                                    o888o
 -->

<script>

import Store from '@/store'
import { TweenMax as Tween, TimelineMax as Line, Power1 } from 'gsap'

import TransitionWrapper from '@/layout/wrappers/transition-wrapper'
import ScrollMarker from '@/components/scroll-marker'
import List from './list'
import TheFooter from '@/layout/the-footer'

export default {

  name: 'NewsPage',

  components: {
    TransitionWrapper,
    ScrollMarker,
    List,
    TheFooter
  },

  computed: {
    $content () {
      return this.$store.state.pages.landings.news
    },

    items () {
      return this.$store.getters['pages/all']('news')
    },

    count () {
      return parseInt(this.$content.count);
    }
  },

  beforeRouteEnter: async (to, from, next) => {

    Store.commit('startLoading')

    const page = await Store.dispatch('pages/fetchLanding', 'news')
    const items = await Store.dispatch('pages/fetchDynamic', {
      name: 'news',
      limit: 7,
      offset:0,
      firstLoad: true
    })

    Store.commit('endLoading')

    const errors = []

    if (!page.title) errors.push('News page must have a title.')
    if (!page.description) errors.push('News page must have a description.')
    if (items.length <= 0) errors.push('News page must have at least one story.')

    if (errors.length > 0) {
      next({
        path: '/' + (from.params.lang || 'ua') + '/error',
        query: { code: 500, errors }
      })
      return
    }

    next()
  },

  beforeRouteUpdate: async (to, from, next) => {
    if (from.params.lang === to.params.lang) {
      next()
      return
    }

    Store.commit('startLoading')

    const page = await Store.dispatch('pages/fetchLanding', 'news');
    const items = await Store.dispatch('pages/fetchDynamic', {
      name: 'news',
      limit: 7,
      offset: 0,
      firstLoad: true
    })

    Store.commit('endLoading')

    const errors = []

    if (!page.title) errors.push('News page must have a title.')
    if (!page.description) errors.push('News page must have a description.')
    if (items.length <= 0) errors.push('News page must have at least one story.')

    if (errors.length > 0) {
      next({
        path: '/' + (from.params.lang || 'ua') + '/error',
        query: { code: 500, errors }
      })
      return
    }

    next()
  },

  created(){
    this.setTitle();
  },

  methods: {

    setTitle(){
      let title = document.getElementsByTagName('title')[0];
      return title.text = this.$content.meta.title;
    },

    loadMore () {
      const lastItem = this.items[this.items.length - 1]
      const timestamp = new Date(lastItem.createdAt).getTime()

      this.$store.dispatch('pages/fetchDynamic', {
        name: 'news',
        limit: 7,
        offset:this.items.length
      })
    },

    enter () {
      const tl = new Line()
      const els = [
        this.$refs.title,
        this.$refs.subtitle
      ]

      tl.staggerFrom(els, 1, {
        autoAlpha: 0,
        y: -40,
        ease: Power1.easeOut
      }, 0.1)
    },

    leave (el, done) {
      if (this.$store.state.app.routeChangedFromPopup) {
        done()
      } else {
        Tween.to(el, 0.5, {
          autoAlpha: 0,
          ease: Power1.easeOut,
          onComplete: done
        })
      }
    },

    scroll () {
      const bb = this.$refs.list.$el.getBoundingClientRect()
      const scroll = this.$store.state.app.window.scroll + bb.top

      Tween.to(window, 1, {
        scrollTo: scroll,
        ease: Power1.easeInOut
      })
    }
  }

}

</script>


<!--

  oooo                                                  .
  `888                                                .o8
   888   .oooo.   oooo    ooo  .ooooo.  oooo  oooo  .o888oo
   888  `P  )88b   `88.  .8'  d88' `88b `888  `888    888
   888   .oP"888    `88..8'   888   888  888   888    888
   888  d8(  888     `888'    888   888  888   888    888 .
  o888o `Y888""8o     .8'     `Y8bod8P'  `V88V"V8P'   "888"
                  .o..P'
                  `Y8P'
 -->


<template lang="pug">

.blog-page

  .intro
    .intro-container
      h1.intro__heading.h1-like(ref="title") {{ $content.title }}
      p.intro__description(ref="subtitle") {{ $content.description }}

    transition-wrapper(:appear="true")
      scroll-marker.intro__scroll(@click="scroll", :dark-mode="true")

  transition-wrapper(:appear="true")
    list(type="news", :items="items", :count="count" ref="list" @load-more-items="loadMore")

  the-footer

</template>


<!--
               .               oooo
             .o8               `888
   .oooo.o .o888oo oooo    ooo  888   .ooooo.   .oooo.o
  d88(  "8   888    `88.  .8'   888  d88' `88b d88(  "8
  `"Y88b.    888     `88..8'    888  888ooo888 `"Y88b.
  o.  )88b   888 .    `888'     888  888    .o o.  )88b
  8""888P'   "888"     .8'     o888o `Y8bod8P' 8""888P'
                   .o..P'
                   `Y8P'
 -->


<style lang="stylus" scoped>

.blog-page
  flex-direction column
  // align-items center

.intro
  position relative
  display flex
  justify-content center
  padding-top 210px
  padding-bottom 120px
  color dark
  +mobile()
    padding-top 73px
    padding-bottom 90px


  +hd()
    padding 280px 0 180px

  &__heading
    & + *
      margin-top 21px

  &__description
    font-weight 500
    font-size 26px
    line-height 24px

  &__scroll
    position absolute
    left 0
    right 0
    bottom 0
    margin 0 auto

.intro-container
  display flex
  flex-direction column
  justify-content center
  max-width 894px
  width 100%

  +hd()
    max-width 1120px

  +tablet()
    padding-left 85px
    padding-right 85px

  +mobile()
    max-width 560px
    padding 20px 30px

</style>



// WEBPACK FOOTER //
// news.vue?72165e2c